import { useState } from 'react';

import { bulkReviewIndirectTaxFilingsV2 } from '@commenda/api/indirect-tax/filings';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@commenda/kakashi';

interface IndirectTaxFilingsBulkReviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  filingIds: string[];
  corporationId: number;
  onReviewed?: () => void;
}

const IndirectTaxFilingsBulkReviewModal = ({
  isOpen,
  onClose,
  filingIds,
  corporationId,
  onReviewed,
}: IndirectTaxFilingsBulkReviewModalProps) => {
  const [isReviewing, setIsReviewing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const count = filingIds.length;

  const handleReview = async () => {
    setIsReviewing(true);
    setError(null);
    const result = await bulkReviewIndirectTaxFilingsV2({
      params: { corporationId, filingIds },
    });
    setIsReviewing(false);
    if (result.success) {
      onReviewed?.();
      onClose();
    } else {
      setError(result.error ?? 'Failed to mark filings as reviewed');
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => (open ? undefined : onClose())}>
      <DialogContent>
        <DialogHeader className='border-none flex-col items-start gap-2'>
          <DialogTitle>Mark selected filings as reviewed?</DialogTitle>
          <DialogDescription>
            Filings currently awaiting agent review will be marked as reviewed
            and handed off to the client for approval. This action supports US
            filings only — if your selection includes a non-US filing, nothing
            will be marked.
          </DialogDescription>
        </DialogHeader>
        {error ? (
          <p className='px-3xl txt-small-regular text-text-error'>{error}</p>
        ) : null}
        <DialogFooter className='border-none'>
          <Button
            size="sm"
            variant="secondary"
            onClick={onClose}
            disabled={isReviewing}
          >
            Cancel
          </Button>
          <Button
            size="sm"
            variant="primary"
            onClick={handleReview}
            disabled={isReviewing || count === 0}
          >
            {isReviewing ? 'Marking...' : 'Mark as reviewed'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export { IndirectTaxFilingsBulkReviewModal };
